import React from "react";
import { Card, CardContent, Typography, Grid, Box } from "@mui/material";
import { formatHoursMinutes } from "../utils/time";
import { useTranslation } from "react-i18next";
import { useCustomerCurrency, useCustomerLocale } from "../context/CustomerSettingsContext";

const getActivityColor = (percentage) => {
  if (percentage <= 50) return 'rgb(255, 0, 0)';
  if (percentage >= 95) return 'rgb(0, 255, 0)';
  const factor = (percentage - 50) / 45;
  if (factor < 0.5) {
    const orangeFactor = factor * 2;
    return `rgb(255, ${Math.round(165 * orangeFactor)}, 0)`;
  } else {
    const greenFactor = (factor - 0.5) * 2;
    return `rgb(${Math.round(255 * (1 - greenFactor))}, ${Math.round(165 + (255 - 165) * greenFactor)}, 0)`;
  }
};

const DailyOverviewGrid = ({
  totalTurnoverOps,
  totalOps,
  totalTurnoverHours,
  totalHours,
  avgActivity,
  totalPossibleHours,
}) => {
  const { t } = useTranslation();
  const { currency, formatCurrency } = useCustomerCurrency();
  const locale = useCustomerLocale();

  const formatCount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return t("common.na");
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(num);
  };

  const formatPercent = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return t("common.na");
    return new Intl.NumberFormat(locale, {
      style: "percent",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(num / 100);
  };

  return (
    <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
      <Grid container spacing={2} justifyContent="center" sx={{ maxWidth: 1200 }} wrap="nowrap">
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography variant="h6">{t("daily.overview.kpi.total.title", { currency })}</Typography>
            <Typography variant="body2" color="text.secondary">({t("daily.overview.kpi.total.subtitle")})</Typography>
            <Typography variant="h4">{formatCurrency(totalTurnoverOps + totalTurnoverHours)}</Typography>
            <Typography color="text.secondary">&nbsp;</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md="1" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="h4" sx={{ mx: 1 }}>=</Typography>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography variant="h6">{t("daily.overview.kpi.operations.title", { currency })}</Typography>
            <Typography variant="body2" color="text.secondary">({t("daily.overview.kpi.operations.subtitle")})</Typography>
            <Typography variant="h4">{formatCurrency(totalTurnoverOps)}</Typography>
            <Typography color="text.secondary">{t("daily.overview.kpi.operations.totalOperations", { count: formatCount(totalOps) })}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md="1" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="h4" sx={{ mx: 1 }}>+</Typography>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Typography variant="h6">{t("daily.overview.kpi.hours.title", { currency })}</Typography>
            <Typography variant="body2" color="text.secondary">({t("daily.overview.kpi.hours.subtitle")})</Typography>
            <Typography variant="h4">{formatCurrency(totalTurnoverHours)}</Typography>
            <Typography color="text.secondary">{t("daily.overview.kpi.hours.totalHours", { duration: formatHoursMinutes(totalHours) })}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card sx={{ borderRight: `12px solid ${getActivityColor(avgActivity)}` }}>
          <CardContent>
            <Typography variant="h6">{t("daily.overview.kpi.activity.title")}</Typography>
            <Typography variant="body2" color="text.secondary">({t("daily.overview.kpi.activity.subtitle")})</Typography>
            <Typography variant="h4">{formatPercent(avgActivity)}</Typography>
            <Typography color="text.secondary">
              {t("daily.overview.kpi.activity.workedVsPossible", {
                worked: formatHoursMinutes(totalHours),
                possible: formatHoursMinutes(totalPossibleHours),
              })}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      </Grid>
    </Box>
  );
};

export default DailyOverviewGrid;
